/**
 * @description 视频播放器
 * @author librajt
 */

import { DRAGGABLE_TYPE, EPlayState } from '@umu/umu-types';
import { ICancelableFunction, interval, EventEmitterWrapper as EventEmitter } from '@umu/umu-util';
import PlayerManager, { EMediaEvent, IMediaOptions, TMediaEvent } from './playerManager';

export interface IVideoPlayerOptions extends IMediaOptions {
    autoplay?: boolean;
    file: string;
    psign?: string;
    appID?: string;
    volume?: number;
    poster?: string;
    controls?: boolean;
    container?: string | Element;
    minBufferSize?: number;
    lastPlayedTime?: number;
    useNativeVideo?: boolean;
    allowChangePlayRate?: boolean;
    draggableType?: DRAGGABLE_TYPE;
}

export type TEventListener = 'playStateUpdate' | 'currentTimeUpdate' | 'playStart' | 'resume' | 'pause';

const isInX5 = /(tbs\/|MQQBrowser)/gi.test(navigator.userAgent);

const timeTracking: Record<string, number> = {
    watchedTime: 0,
    currentTime: 0,
};
let lastUpdated: string = 'currentTime';

export default class BaseVideoPlayer extends EventEmitter {
    protected options: IVideoPlayerOptions;
    protected container: HTMLElement | null = null;
    protected playState: EPlayState = EPlayState.buffering;
    protected videoPlayer: HTMLVideoElement | null = null;
    protected playerId: string = '';
    private timeListener?: ICancelableFunction;
    private isPlayerStart: boolean = false;
    private isAllowJumpWhenStartPlaying = true; // 开始播放时允许跳转到上次播放
    public isPlayerSeek: boolean = false;
    public videoEventMap: { [key: string]: (e: Event[]) => void } = {};

    constructor(options: IVideoPlayerOptions) {
        super();
        this.options = Object.assign(
            {
                autoplay: false,
                file: '', // 播放文件路径
                volume: 100,
                // 微信环境内使用原生默认的video控件，以达到全屏的效果
                controls: options.useNativeVideo,
                container: 'body',
                minBufferSize: 1, // 剩下的buffer最少为多少秒
                useNativeVideo: false,
                allowChangePlayRate: true,
            },
            options,
        );
        const container = this.options.container;
        if (container && typeof container === 'string') {
            const containerEl = document.querySelector(container) as HTMLElement;
            if (containerEl) {
                this.container = containerEl;
            }
        }
        // 如果 container 是 Element 类型，直接使用
        if (container && container instanceof HTMLElement) {
            this.container = container;
        }

        if (!this.container) {
            throw new Error('没有 container ，初始化 videoPlayer 失败');
        }
        this.init();
    }

    public getVideEle() {
        return this.videoPlayer!;
    }

    // =============================== public method ==================================
    public on(eventName: TEventListener | TMediaEvent, listener: (...args: any[]) => void) {
        if (this.videoEventMap[eventName]) {
            this.videoPlayer && this.videoPlayer.addEventListener(eventName, listener, false);
        } else {
            super.on(eventName, listener);
        }
    }

    public off(eventName?: TEventListener | TMediaEvent, listener?: (...args: any[]) => void) {
        if (eventName && listener && this.videoEventMap[eventName]) {
            this.videoPlayer && this.videoPlayer.removeEventListener(eventName, listener, false);
        } else {
            super.off(eventName, listener);
        }
    }

    public play() {
        if (this.videoPlayer && this.videoPlayer.paused) {
            // 避免播放到结尾后，再次点击播放，直接触发 ended 事件
            if (this.getCurrentTime() >= this.getDuration()) {
                this.fastSeek(0);
            }
            PlayerManager.play(this.playerId);
        }
    }

    public isPaused() {
        return this.videoPlayer && this.videoPlayer.paused;
    }

    public togglePlay() {
        if (this.videoPlayer && this.videoPlayer.paused) {
            this.play();
        } else {
            PlayerManager.pause(this.playerId);
        }
    }

    public pause() {
        PlayerManager.pause(this.playerId);
    }

    public stop() {
        if (this.videoPlayer) {
            this.fastSeek(0);
        }
        PlayerManager.pause(this.playerId);
        this.dispatchCurrentTime(false);
        if (isInX5) {
            this.reset();
        }
    }

    public fastSeek(time: number) {
        if (!this.videoPlayer) {
            return;
        }
        if (typeof this.videoPlayer.fastSeek === 'function') {
            this.videoPlayer.fastSeek(time);
        }
        this.videoPlayer.currentTime = time;
    }

    public seekTime(time: number) {
        if (this.videoPlayer) {
            this.fastSeek(time);
        }
        this.dispatchCurrentTime(false);
    }

    public seekPercent(percent: number) {
        const duration = this.videoPlayer ? this.videoPlayer.duration : 0;
        const time = duration * percent;
        this.seekTime(time);
    }

    public setPlaybackRate(rate: number) {
        if (!this.videoPlayer) {
            return;
        }
        this.videoPlayer.playbackRate = rate;
    }

    public destroy() {
        this.unbindEvent();
        if (this.container && this.videoPlayer) {
            this.container.removeChild(this.videoPlayer);
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete this.videoPlayer;
        if (this.timeListener) {
            this.timeListener.cancel();
        }
        this.off();
    }

    public getCurrentTime() {
        if (!this.videoPlayer) {
            return 0;
        }
        return this.videoPlayer.currentTime || 0;
    }

    public getPlayState() {
        return this.playState;
    }

    protected init() {
        this.initVideo();
        this.fixupMediaType(this.options.file);
        this.initVideoEventMap();
        this.bindEvent();
    }

    protected initVideoEventMap() {
        this.videoEventMap = {
            [EMediaEvent.playing]: this.onPlaying.bind(this),
            [EMediaEvent.seeking]: this.onSeeking.bind(this),
            [EMediaEvent.canplay]: this.onCanplay.bind(this),
            [EMediaEvent.loadedmetadata]: this.onLoadMetaData.bind(this),
            [EMediaEvent.waiting]: this.onWaiting.bind(this),
            [EMediaEvent.stalled]: this.onStalled.bind(this),
            [EMediaEvent.loadstart]: this.onLoadstart.bind(this),
            [EMediaEvent.timeupdate]: this.onTimeupdate.bind(this),
            [EMediaEvent.ratechange]: this.onRateChange.bind(this),
            [EMediaEvent.pause]: this.onPause.bind(this),
            [EMediaEvent.ended]: this.onEnded.bind(this),
            [EMediaEvent.error]: this.onError.bind(this),
            [EMediaEvent.touchstart]: this.onTouchstart.bind(this),
        };
    }

    protected initVideo() {
        this.playerId = PlayerManager.add('video', this.options);
        this.videoPlayer = PlayerManager.getPlayer('video', this.playerId) as HTMLVideoElement;
        PlayerManager.load(this.playerId);
        const video = this.videoPlayer;
        if (!video) {
            console.error('initVideo error: video is null');
            return;
        }
        video.setAttribute('controlslist', 'nodownload');
        try {
            this.container?.appendChild(video);
        } catch (error) {
            console.error('appendChild video error', error, video);
        }
    }

    protected bindEvent() {
        for (const eventName in this.videoEventMap) {
            if (this.videoEventMap.hasOwnProperty(eventName)) {
                PlayerManager.on(eventName + '-' + this.playerId, this.videoEventMap[eventName]);
            }
        }

        // 因 timeupdate 有兼容性问题且不一致，使用 timeListener 进行统一
        // fixme: timeupdate 是被广泛支持的，现在已经不对 ie 等低端浏览器做兼容了，这段逻辑还需要保留吗？
        /**
         * 此处特别说明一下：
         * 1 播放时间驱动，一般来说，有两种可用方式: (1)video timeupdate 事件，(2)定时器自定义更新频率；
         * 2 timeupdate 的频率大概是250ms触发一次，这个触发频率在一些情况会比较慢，比如某两次触发点： 654.820076 和 655.08515；另一问题是timeupdate来自video内部的实现，如果其内部视频解码性能压力大，该频率可能会降低。
         * 3 更新频率，不同的浏览器实现可能不一致，所有有浏览器兼容问题。Chrome为什么选择250ms，chromium 官方说法是: The spec says to fire periodic timeupdate events (those sent while playing) every "15 to 250ms", we choose the slowest frequency. [ref](https://github.com/chromium/chromium/blob/main/third_party/blink/renderer/core/html/media/html_media_element.cc)
         * 4 自定义频率的时间可以灵活设置，保证UI更新和视频播放进度保持一致（比如5s的视频，播放条的流畅性可以看出来），也隐含了性能问题。
         * 5 这里定义125ms是250的1/2
         * 6 ChatGPT-4o:[prompt](你是Chrome开发专家，精通chromium源代码，请深入分析下video中timeupdate事件的触发过程)
         */
        this.timeListener = interval(() => {
            this.dispatchCurrentTime(true);
        }, 125);

        this.on('playStateUpdate', (nextState, preState) => {
            if (nextState === preState) {
                return;
            }
            if (this.timeListener) {
                if (nextState === EPlayState.playing) {
                    this.timeListener();
                } else if (nextState !== EPlayState.buffering) {
                    this.timeListener.cancel();
                }
            }
        });
    }

    // =============================== video event ==================================
    private onPlaying() {
        // 原生api没有提供 开始播放 的状态，这里通过标记本地播放状态 isPlayerStart 实现
        if (!this.isPlayerStart) {
            this.emit('playStart');
            this.isPlayerStart = true;
        } else if (this.isPlayerSeek) {
            this.emit('resume');
            this.isPlayerSeek = false;
        } else if (this.playState !== EPlayState.idle) {
            this.emit('resume');
        }

        this.setPlayState(EPlayState.playing);
    }

    private onSeeking() {
        if (!this.options.useNativeVideo) {
            return;
        }
        if (this.options.draggableType === DRAGGABLE_TYPE.DRAGGABLE_PLAYED) {
            // intent0: 开始播放时，忽略intent1的逻辑，如果上次已经播放，从该位置继续播放。
            if (this.isAllowJumpWhenStartPlaying && this.options.lastPlayedTime != 0) {
                this.isAllowJumpWhenStartPlaying = false;
                return;
            }
            const delta = this.getVideEle().currentTime - timeTracking.watchedTime;
            if (delta > 0) {
                this.getVideEle().pause();
                // intent1: play back from where the user started seeking after rewind or without rewind
                this.fastSeek(timeTracking[lastUpdated]);
                this.getVideEle().play();
            }
        } else if (this.options.draggableType === DRAGGABLE_TYPE.WHOLE) {
            // do nothing
        }
    }

    private onRateChange() {
        // 不允许切换播放速率时，强制恢复到1倍速
        if (!this.options.allowChangePlayRate) {
            this.setPlaybackRate(1);
        }
    }

    private onCanplay() {
        this.setPlayState(EPlayState.idle);
    }

    private onLoadMetaData() {
        this.setPlayState(EPlayState.idle);
        this.dispatchCurrentTime(true);
    }

    private onWaiting() {
        this.setPlayState(EPlayState.buffering);
    }

    private onStalled() {
        // 网速较差时, 边播边下时, 通过这个清除playing状态
        if (this.getPlayState() === EPlayState.playing) {
            this.setPlayState(EPlayState.buffering);
        }
    }

    private onLoadstart() {
        this.setPlayState(EPlayState.buffering);
    }

    private onTimeupdate() {
        if (!this.getVideEle().seeking) {
            if (this.getVideEle().currentTime > timeTracking.watchedTime) {
                timeTracking.watchedTime = this.getVideEle().currentTime;
                lastUpdated = 'watchedTime';
            } else {
                //tracking time updated  after user rewinds
                timeTracking.currentTime = this.getVideEle().currentTime;
                lastUpdated = 'currentTime';
            }
        }

        let customState: EPlayState | undefined;
        // 网速较差时, 边播边下时, 通过这个清除buffering状态
        if (this.getPlayState() === EPlayState.buffering) {
            customState = EPlayState.playing;
        }
        // buffer只剩下minBufferSize时
        if (this.getPlayState() === EPlayState.playing) {
            const buffer = this.getBuffer();
            const position = this.getCurrentTime();
            if (buffer && buffer - position <= (this.options.minBufferSize || 1)) {
                customState = EPlayState.buffering;
            }
        }
        // 避免多次set
        if (customState) {
            this.setPlayState(customState);
        }
    }

    private onPause() {
        this.setPlayState(EPlayState.paused);
        this.emit('pause');
    }

    public onEnded() {
        this.setPlayState(EPlayState.idle);
        this.isPlayerStart = false;
    }

    private onError(e: Event[]) {
        console.error(e);
    }

    // 修复 chrome 浏览器中长按视频，可下载视频问题，https://app.asana.com/0/667465608023199/1200946863067251
    private onTouchstart(e: Event[]) {
        if (this.options.useNativeVideo) {
            return;
        }

        return e[0] && e[0].preventDefault();
    }

    // =============================== private method ==================================
    private getBuffer() {
        const video = this.getVideEle();
        return (video.buffered.length && video.buffered.end(video.buffered.length - 1)) || 0;
    }

    private getDuration() {
        if (!this.videoPlayer) {
            return 0;
        }
        return this.videoPlayer.duration || 0;
    }

    private dispatchCurrentTime(isInitial: boolean) {
        this.emit('currentTimeUpdate', this.getCurrentTime(), this.getDuration(), isInitial);
    }

    private setPlayState(nextState: EPlayState) {
        const preState = this.getPlayState();
        this.playState = nextState;
        this.emit('playStateUpdate', nextState, preState);
    }

    private reset() {
        this.destroy();
        this.initVideo();
        this.bindEvent();
    }

    private unbindEvent() {
        for (const eventName in this.videoEventMap) {
            if (this.videoEventMap.hasOwnProperty(eventName)) {
                PlayerManager.off(eventName + '-' + this.playerId, this.videoEventMap[eventName]);
            }
        }
    }

    // 视频里，有mp3的音频，导致在小米微信上出现闪屏的情况
    private fixupMediaType = (file: string) => {
        if (!file || !/.mp3$/i.test(file)) {
            return;
        }
        PlayerManager.removeAttribute(this.getVideEle(), 'x5-video-player-type');
    };
}
